import { Box } from '@bymason-private/legacy-pattern-components';
import { useToast } from '../../';
import {
  useGetAccount,
  useGetAccountPermissions,
  AccountModel,
  UserModel,
  PermissionSet,
} from '@bymason/controller-api-hooks';
import { useAuth } from '@bymason-private/auth-provider';
import { useCallback, useEffect, useState } from 'react';

import { EditUserModal } from '../modals/edit-user-modal';
import { InviteUserModal } from '../modals/invite-user-modal';
import { UserList } from './user-list';

interface UsersViewProps {
  showInviteUserModal?: boolean;
  onCloseInviteUserModal?: () => void;
}

export function UsersView({
  showInviteUserModal = false,
  onCloseInviteUserModal = () => undefined,
}: UsersViewProps) {
  const { authToken = '' } = useAuth();
  const [getAccount, account, accountError, accountBusy] = useGetAccount(authToken);
  const [
    getAccountPermissions,
    accountPermissions,
    accountPermissionsError,
    accountPermissionsBusy,
  ] = useGetAccountPermissions(authToken);
  const [setToastMessage, setToastSeverity] = useToast();

  const [permissionedAccount, setPermissionedAccount] = useState<AccountModel>();
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [userEditModalTarget, setUserEditModalTarget] = useState<UserModel>(); // TODO: find a better name. Tracks the user that is to be edited

  const handleEditUserModalOpen = (user: UserModel) => {
    setShowEditUserModal(true);
    setUserEditModalTarget(user);
  };

  const handleEditUserModalClose = () => {
    setShowEditUserModal(false);
  };

  const handleChangeUsersError = useCallback(
    (message) => {
      setToastSeverity('error');
      setToastMessage(message);
    },
    [setToastMessage, setToastSeverity]
  );

  const fetchData = useCallback(() => {
    getAccount({});
    getAccountPermissions({});
  }, [getAccount, getAccountPermissions]);

  const handleInviteUserSuccess = useCallback(
    (email: string) => {
      setToastMessage(`Successfully invited ${email}`);
      setToastSeverity('success');
      fetchData();
    },
    [fetchData, setToastMessage, setToastSeverity]
  );

  const handleSuccess = useCallback(
    (user: UserModel, actionType: 'deleted' | 'updated') => {
      setToastMessage(`Successfully ${actionType} permissions for ${user.name}`);
      setToastSeverity('success');
      fetchData();
    },
    [fetchData, setToastMessage, setToastSeverity]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!accountBusy && accountError) {
      setToastMessage(accountError);
      setToastSeverity('error');
    }
  }, [accountBusy, accountError, setToastMessage, setToastSeverity]);

  useEffect(() => {
    if (!accountPermissionsBusy && accountPermissionsError) {
      setToastMessage(accountPermissionsError);
      setToastSeverity('error');
    }
  }, [accountPermissionsBusy, accountPermissionsError, setToastMessage, setToastSeverity]);

  useEffect(() => {
    if (accountPermissions && account && !accountPermissionsBusy && !accountBusy) {
      setPermissionedAccount(mapAccountPermissions(account, accountPermissions.data ?? []));
    }
  }, [accountPermissions, account, accountPermissionsBusy, accountBusy]);

  return (
    <Box>
      <UserList users={permissionedAccount?.users || []} handleEditUserModalOpen={handleEditUserModalOpen} />
      {userEditModalTarget && showEditUserModal && (
        <EditUserModal
          open={showEditUserModal}
          user={userEditModalTarget}
          onClose={handleEditUserModalClose}
          onSuccess={handleSuccess}
          onError={handleChangeUsersError}
        />
      )}
      {account && showInviteUserModal && (
        <InviteUserModal
          open={showInviteUserModal}
          account={account}
          onClose={onCloseInviteUserModal}
          onSuccess={handleInviteUserSuccess}
          onError={handleChangeUsersError}
        />
      )}
    </Box>
  );
}

function mapAccountPermissions(account: AccountModel, permission: PermissionSet[]): AccountModel {
  const { users } = account;

  const mappedPermissions = permission.map((p) => ({
    ...p,
  }));

  const permissionedAccounts = users.map((user) => {
    const { user_id } = user;
    const userPermission = mappedPermissions.filter((p) => p.userid === user_id)[0];
    if (userPermission) {
      return {
        ...user,
        scopes: userPermission.scopes,
      };
    }
    return {
      ...user,
    };
  });

  return {
    ...account,
    users: permissionedAccounts,
  };
}
